import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, take } from 'rxjs';
import {
  AgentService,
  AgentSelfieCheckinDto,
  AgentSelfieUrlDto,
  ZoneDto,
} from '../../../services/agent.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-agent-selfie-checkins',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-selfie-checkins.component.html',
  styleUrl: './agent-selfie-checkins.component.css',
})
export class AgentSelfieCheckinsComponent implements OnInit {
  checkins: AgentSelfieCheckinDto[] = [];
  zones: ZoneDto[] = [];
  isLoading = false;
  selectedDate: string = this.todayIso();
  selectedZoneId = '';

  // Selfie lightbox
  selfieUrl: string | null = null;
  selfieAgentName: string | null = null;
  loadingSelfieFor: string | null = null;
  selfieError: string | null = null;

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info',
  };

  constructor(
    private agentService: AgentService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService
      .getMsalInitializedObservable()
      .pipe(
        filter((initialized) => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadZones();
        this.loadCheckIns();
      });
  }

  loadZones(): void {
    this.agentService.getAllZones().subscribe({
      next: (zones) => (this.zones = zones),
      error: () => {},
    });
  }

  loadCheckIns(): void {
    this.isLoading = true;
    const date = this.selectedDate || undefined;
    const zoneId = this.selectedZoneId || undefined;

    this.agentService.getSelfieCheckIns(date, zoneId).subscribe({
      next: (data) => {
        this.checkins = data;
        this.isLoading = false;
      },
      error: () => {
        this.showNotification('Failed to load check-in data.', 'error');
        this.isLoading = false;
      },
    });
  }

  onFilterChange(): void {
    this.loadCheckIns();
  }

  getZoneName(zoneId: string): string {
    return this.zones.find((z) => z.id === zoneId)?.name ?? zoneId;
  }

  getZoneNames(zoneIds: string[]): string {
    if (!zoneIds || zoneIds.length === 0) return '—';
    return zoneIds.map((id) => this.getZoneName(id)).join(', ');
  }

  get checkedInCount(): number {
    return this.checkins.filter((c) => c.hasCheckedIn).length;
  }

  viewSelfie(checkin: AgentSelfieCheckinDto): void {
    if (this.loadingSelfieFor === checkin.agentId) return;
    this.selfieUrl = null;
    this.selfieError = null;
    this.selfieAgentName = checkin.name;
    this.loadingSelfieFor = checkin.agentId;

    const date = this.selectedDate || undefined;
    this.agentService.getAgentSelfieUrl(checkin.agentId, date).subscribe({
      next: (result: AgentSelfieUrlDto) => {
        if (result.selfieUrl === 'placeholder_manual_override') {
          this.selfieError = 'This check-in was manually approved by the Control Center. No selfie image is available.';
        } else {
          this.selfieUrl = result.selfieUrl;
        }
        this.loadingSelfieFor = null;
      },
      error: () => {
        this.selfieError = 'Could not load selfie. The image may not be available.';
        this.loadingSelfieFor = null;
      },
    });
  }

  closeSelfie(): void {
    this.selfieUrl = null;
    this.selfieError = null;
    this.selfieAgentName = null;
  }

  overrideCheckin(checkin: AgentSelfieCheckinDto): void {
    if (
      confirm(`Are you sure you want to manually approve the selfie check-in for ${checkin.name}?`)
    ) {
      this.isLoading = true;
      const date = this.selectedDate || undefined;
      this.agentService.overrideAgentSelfie(checkin.agentId, date).subscribe({
        next: () => {
          this.showNotification(`Manually approved check-in for ${checkin.name}.`, 'success');
          this.loadCheckIns();
        },
        error: (err) => {
          const errMsg = err?.error?.error || 'Failed to override check-in status.';
          this.showNotification(errMsg, 'error');
          this.isLoading = false;
        },
      });
    }
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    this.notification = { show: true, message, type };
    setTimeout(() => {
      if (this.notification.message === message) this.notification.show = false;
    }, 4000);
  }

  closeNotification(): void {
    this.notification.show = false;
  }

  private todayIso(): string {
    return new Date().toISOString().substring(0, 10);
  }
}
